import { Award, Heart, Leaf, Users } from "lucide-react";

import { Layout } from "@/components/layout";

export function About() {
  const values = [
    {
      icon: Heart,
      title: "Heartfelt Hospitality",
      description:
        "We treat every guest as family, creating warm, memorable experiences that last a lifetime.",
    },
    {
      icon: Award,
      title: "Excellence",
      description:
        "From our villas to our service, we maintain the highest standards in everything we do.",
    },
    {
      icon: Leaf,
      title: "Sustainability",
      description:
        "Committed to preserving Samui's natural beauty for future generations through eco-friendly practices.",
    },
    {
      icon: Users,
      title: "Community",
      description:
        "Supporting local artisans, farmers, and businesses to strengthen our island community.",
    },
  ];

  const milestones = [
    { year: "2015", event: "The Amor opens its doors with 8 exclusive beachfront villas" },
    { year: "2017", event: 'Awarded "Best Boutique Resort in Thailand" by Travel + Leisure' },
    { year: "2019", event: "Launch of our world-class spa and wellness center" },
    { year: "2021", event: "Achieved Green Hotel Certification for sustainable practices" },
    { year: "2023", event: "Recognized in Condé Nast Traveler's Gold List" },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/594077/pexels-photo-594077.jpeg?auto=compress&cs=tinysrgb&w=1920)",
          }}
        >
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="relative h-full flex items-center justify-center text-center px-6">
          <div className="max-w-3xl">
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight">
              Our Story
            </h1>
            <p className="text-xl text-white/95 font-light tracking-wide">
              A love letter to Samui, crafted with passion and purpose
            </p>
          </div>
        </div>
      </section>

      {/* Story Introduction */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-6 text-center">
            Welcome to The Amor
          </h2>
          <div className="w-24 h-px bg-luxury-gold mx-auto mb-8" />

          <div className="space-y-6 text-lg text-stone-700 leading-relaxed">
            <p>
              The Amor was born from a deep love for Koh Samui and a vision to create something
              extraordinary - a place where the timeless beauty of Thailand meets contemporary
              luxury, where every detail tells a story, and where guests become family.
            </p>
            <p>
              Our journey began with a simple belief: that true luxury isn't found in excess, but in
              thoughtful design, genuine warmth, and unforgettable experiences. We wanted to create
              an intimate sanctuary where just eight beachfront villas would offer our guests the
              privacy, exclusivity, and personalized service they deserve.
            </p>
            <p>
              Today, The Amor stands as a testament to that vision - a haven of tranquility on one
              of Samui's most pristine beaches, where the rhythm of the waves sets the pace for
              relaxation, and every sunset is a celebration.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-6 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 text-center mb-4">
            Our Values
          </h2>
          <p className="text-center text-stone-600 mb-12 max-w-2xl mx-auto">
            The principles that guide everything we do at The Amor
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="bg-white p-6 border border-stone-200 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-stone-900 text-white mb-4">
                    <Icon size={28} />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-stone-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-stone-600 text-sm leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Journey/Timeline */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 text-center mb-4">
            Our Journey
          </h2>
          <div className="w-24 h-px bg-luxury-gold mx-auto mb-12" />

          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="flex gap-8 items-start">
                <div className="flex-shrink-0 w-20">
                  <span className="font-heading text-2xl font-bold text-luxury-gold">
                    {milestone.year}
                  </span>
                </div>
                <div className="flex-1 border-l-2 border-stone-200 pl-8 pb-8">
                  <p className="text-stone-700 leading-relaxed">{milestone.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 px-6 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 text-center mb-4">
            Our Team
          </h2>
          <div className="w-24 h-px bg-luxury-gold mx-auto mb-12" />

          <div className="max-w-3xl mx-auto text-center">
            <p className="text-lg text-stone-700 leading-relaxed mb-8">
              Behind The Amor is a dedicated team of hospitality professionals who share a passion
              for creating exceptional experiences. From our concierge who knows every hidden gem on
              the island, to our chefs who craft culinary masterpieces daily, to our spa therapists
              trained in ancient Thai healing arts - every team member is committed to making your
              stay unforgettable.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed">
              Many of our team members have been with us since day one, and their knowledge,
              expertise, and genuine care for our guests are what truly set The Amor apart.
            </p>
          </div>
        </div>
      </section>

      {/* Sustainability */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-6">
                Sustainability & Community
              </h2>
              <div className="w-24 h-px bg-luxury-gold mb-8" />

              <p className="text-stone-700 leading-relaxed mb-6">
                Koh Samui is our home, and we're committed to protecting its natural beauty and
                supporting its community. Our sustainability initiatives include:
              </p>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-luxury-gold rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <strong className="text-stone-900">Solar Power:</strong>
                    <span className="text-stone-700">
                      {" "}
                      60% of our energy comes from renewable sources
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-luxury-gold rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <strong className="text-stone-900">Water Conservation:</strong>
                    <span className="text-stone-700">
                      {" "}
                      Advanced filtration and recycling systems
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-luxury-gold rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <strong className="text-stone-900">Local Sourcing:</strong>
                    <span className="text-stone-700">
                      {" "}
                      80% of our food comes from local farms and fishermen
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-luxury-gold rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <strong className="text-stone-900">Zero Plastic:</strong>
                    <span className="text-stone-700">
                      {" "}
                      Eliminated single-use plastics throughout the resort
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-luxury-gold rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <strong className="text-stone-900">Community Support:</strong>
                    <span className="text-stone-700">
                      {" "}
                      Regular donations to local schools and environmental initiatives
                    </span>
                  </div>
                </li>
              </ul>

              <p className="text-stone-700 leading-relaxed">
                We believe luxury and responsibility go hand in hand, and we're constantly finding
                new ways to reduce our environmental impact while supporting the local community.
              </p>
            </div>

            <div className="relative h-[500px]">
              <img
                src="https://images.pexels.com/photos/13281334/pexels-photo-13281334.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Samui Natural Beauty"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recognition */}
      <section className="py-16 px-6 bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">Recognition & Awards</h2>
          <div className="w-24 h-px bg-luxury-gold mx-auto mb-8" />

          <p className="text-lg text-stone-300 leading-relaxed mb-8">
            We're honored to be recognized by leading travel publications and organizations, but our
            greatest reward is the smile on our guests' faces and the memories they create at The
            Amor.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div className="border border-stone-700 p-6">
              <Award className="mx-auto mb-3 text-luxury-gold" size={32} />
              <p className="font-semibold mb-1">Travel + Leisure</p>
              <p className="text-stone-400">Best Boutique Resort 2017</p>
            </div>
            <div className="border border-stone-700 p-6">
              <Award className="mx-auto mb-3 text-luxury-gold" size={32} />
              <p className="font-semibold mb-1">Condé Nast Traveler</p>
              <p className="text-stone-400">Gold List 2023</p>
            </div>
            <div className="border border-stone-700 p-6">
              <Award className="mx-auto mb-3 text-luxury-gold" size={32} />
              <p className="font-semibold mb-1">Green Hotel</p>
              <p className="text-stone-400">Certified 2021</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
